import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Ban, Boxes, GitBranch, Package, ShieldAlert } from 'lucide-react';
import { DataTable, type DataFetchParams } from '@/components/DataTable';
import { MetricCard } from '@/components/ui/metric-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  AggregatedPackage,
  AggregatedRepository,
  PackageListStateFilter,
} from '@kirin/api-client/contracts/agent-command-events';
import { useAgentCommandEventsStats } from '@/hooks/api/useAgentCommandEvents';
import { usePackageListActions } from '@/hooks/api/usePackageListActions';
import {
  packageGroupRuleItem,
  useInventoryGroupRuleActions,
} from '@/hooks/api/useInventoryGroupRuleActions';
import { useInventoryScope } from '@/hooks/useInventoryScope';
import { Skeleton } from '@/components/ui/skeleton';
import { GroupScopeResetButton } from '@/components/shared/GroupScopeResetButton';
import { packagesGroupScopeReset } from '@/hooks/api/useGroupScopeReset';
import { PolicyScopeSelector } from '../../components/PolicyScopeSelector';
import { InventoryScopeUnavailableNotice } from '../../components/InventoryScopeUnavailableNotice';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { usePackageListsAvailable } from '@/hooks/usePackageListsAvailable';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { PackageListStateFilterControl } from './PackageListStateFilterControl';
import {
  createAggregatedPackageColumns,
  createAggregatedRepositoryColumns,
} from './columns';
import {
  aggregatedPackagesQueryKey,
  aggregatedRepositoriesQueryKey,
  fetchAggregatedPackages,
  fetchAggregatedRepositories,
} from './api';
import {
  AggregatedPackageDetailDialog,
  AggregatedRepositoryDetailDialog,
  PackageEndpointsDialog,
  PackageOccurrencesDialog,
  PackageUsersDialog,
  RepositoryEndpointsDialog,
  RepositoryOccurrencesDialog,
  RepositoryUsersDialog,
} from './AggregatedPackageDialogs';
import {
  WhatIsTrackedDialog,
  type AgentsActivityTabValue,
} from './WhatIsTrackedDialog';
import {
  AGENT_COMMAND_PAGE_SIZE,
  AGENT_COMMAND_TABLE_IDS,
  DEFAULT_TIME_RANGE,
  TIME_RANGE_OPTIONS,
  timeRangeToFromIso,
  type TimeRangeValue,
} from './constants';

/**
 * Agents Activity → Packages & Repos (KIR-3483). Two tabs (Packages default /
 * Repositories), stat cards, a time-range filter, sortable + paginated tables,
 * a row → detail dialog, and the KNOWN-BAD red left-stripe. One component drives
 * both the org view (organizationId from the route) and the personal view (no
 * organizationId → personal-account API).
 *
 * ponytail: agent/user multi-select filters from the ticket are deferred — v1 is
 * Claude-only (one agent) and there is no user-options endpoint yet. Time-range
 * + free-text search cover the useful cases; the query plumbing already carries
 * agentNames/userIds for when those selects land.
 */
export default function AgentsActivityTab(): ReactElement {
  const { organizationId } = useParams<{ organizationId?: string }>();
  const [timeRange, setTimeRange] =
    useState<TimeRangeValue>(DEFAULT_TIME_RANGE);
  // Drives the per-tab "What is tracked?" text (KIR-4848).
  const [activeTab, setActiveTab] =
    useState<AgentsActivityTabValue>('packages');
  // Aggregated views (KIR-3807): each dialog tracks its selected row.
  const [detailPackage, setDetailPackage] = useState<AggregatedPackage | null>(
    null
  );
  const [occurrencesPackage, setOccurrencesPackage] =
    useState<AggregatedPackage | null>(null);
  const [usersPackage, setUsersPackage] = useState<AggregatedPackage | null>(
    null
  );
  const [endpointsPackage, setEndpointsPackage] =
    useState<AggregatedPackage | null>(null);
  const [detailRepo, setDetailRepo] = useState<AggregatedRepository | null>(
    null
  );
  const [occurrencesRepo, setOccurrencesRepo] =
    useState<AggregatedRepository | null>(null);
  const [usersRepo, setUsersRepo] = useState<AggregatedRepository | null>(null);
  const [endpointsRepo, setEndpointsRepo] =
    useState<AggregatedRepository | null>(null);

  // KIR-4443 (AC 10). Page-level rather than a DataTable column filter — see
  // AgentCommandExtraFilters.listState.
  const [selectedListState, setListState] =
    useState<PackageListStateFilter>('all');

  const { data: stats } = useAgentCommandEventsStats(organizationId);

  // KIR-4443: the Enforcement column, its filter and the Block actions
  // describe the packages-management rules, so they only appear where those
  // rules are what the scanner applies (see usePackageListsAvailable).
  const { available: showPackageLists, isLoading: packageListsLoading } =
    usePackageListsAvailable(organizationId);
  const listState: PackageListStateFilter = showPackageLists
    ? selectedListState
    : 'all';

  // Owners and managers edit; support sees the lists read-only; everyone
  // else, and anyone while roles are still loading, cannot edit.
  const writeGate = useOrgWriteGate(organizationId);
  const canEditPackageLists =
    !writeGate.hidden && !writeGate.disabled && !writeGate.isLoading;

  const packageListActions = usePackageListActions(
    organizationId as OrganizationId | undefined,
    'packages_page'
  );

  // KIR-4701 Allow/Block scope, for the Packages tab's Enforcement column
  // only: Repositories has no Allow/Block.
  const scope = useInventoryScope(organizationId);
  const scopeSelectable = showPackageLists && scope.selectorVisible;
  const securityGroupId = showPackageLists ? scope.groupId : null;
  const scopeGroupName = securityGroupId ? scope.groupName : null;
  const groupActions = useInventoryGroupRuleActions(
    organizationId ?? '',
    securityGroupId,
    packageGroupRuleItem
  );
  const packageGroup = useMemo(
    () =>
      securityGroupId && scopeGroupName
        ? { id: securityGroupId, name: scopeGroupName, actions: groupActions }
        : undefined,
    [securityGroupId, scopeGroupName, groupActions]
  );

  const from = useMemo(() => timeRangeToFromIso(timeRange), [timeRange]);
  const extra = useMemo(() => ({ from }), [from]);
  // Separate from `extra` so the Repositories table's query key does not
  // change when a packages-only filter moves, which would refetch it for
  // nothing.
  const packagesExtra = useMemo(
    () => ({ from, listState, securityGroupId }),
    [from, listState, securityGroupId]
  );

  const packageColumns = useMemo(
    () =>
      createAggregatedPackageColumns(organizationId, {
        onShowOccurrences: setOccurrencesPackage,
        onShowUsers: setUsersPackage,
        onShowEndpoints: setEndpointsPackage,
        packageListActions,
        canEditPackageLists,
        showPackageLists,
        packageGroup,
      }),
    [
      organizationId,
      packageListActions,
      canEditPackageLists,
      showPackageLists,
      packageGroup,
    ]
  );
  const gitColumns = useMemo(
    () =>
      createAggregatedRepositoryColumns(organizationId, {
        onShowOccurrences: setOccurrencesRepo,
        onShowUsers: setUsersRepo,
        onShowEndpoints: setEndpointsRepo,
      }),
    [organizationId]
  );

  const getRowClassName = useCallback(
    (row: { knownBad: boolean }) =>
      row.knownBad ? 'border-l-4 border-l-destructive' : undefined,
    []
  );

  const initialState = useMemo(
    () => ({ pagination: { pageIndex: 0, pageSize: AGENT_COMMAND_PAGE_SIZE } }),
    []
  );

  const packagesQueryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchAggregatedPackages(organizationId, packagesExtra, params, signal),
    [organizationId, packagesExtra]
  );
  const packagesQueryKeys = useCallback(
    (params: DataFetchParams) =>
      aggregatedPackagesQueryKey(organizationId, packagesExtra, params),
    [organizationId, packagesExtra]
  );

  const reposQueryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchAggregatedRepositories(organizationId, extra, params, signal),
    [organizationId, extra]
  );
  const reposQueryKeys = useCallback(
    (params: DataFetchParams) =>
      aggregatedRepositoriesQueryKey(organizationId, extra, params),
    [organizationId, extra]
  );

  // KIR-4967: same viewport-bounded layout as Alerts (KIR-2748). The tables
  // flex into the space left under the stat cards and scroll inside
  // themselves, so the pagination bar stays on screen instead of the page
  // scrolling past it. The min height (the cards, which wrap to two rows
  // from sm to lg, plus the table chrome and about four rows) stops a short
  // window squeezing the table to nothing and clipping the bar out of reach:
  // below it the page scrolls instead.
  return (
    <div className="flex flex-col gap-6 h-[calc(100vh-11rem)] min-h-[44rem] lg:min-h-[34rem] overflow-hidden">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          variant="info"
          label="Packages installed (24h)"
          value={stats?.packageEvents24h ?? 0}
          icon={Package}
        />
        <MetricCard
          variant="info"
          label="Repos accessed (24h)"
          value={stats?.gitEvents24h ?? 0}
          icon={GitBranch}
        />
        <MetricCard
          variant="danger"
          label="Known-bad detected (24h)"
          value={stats?.knownBadDetected24h ?? 0}
          icon={ShieldAlert}
          muteWhenZero
        />
        <MetricCard
          variant="neutral"
          label="Blocked today"
          value={stats?.blockedToday ?? 0}
          icon={Ban}
          muteWhenZero
        />
        <MetricCard
          variant="neutral"
          label="Unique sources / repos (7d)"
          value={stats?.uniqueSourcesRepos7d ?? 0}
          icon={Boxes}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={value => setActiveTab(value as AgentsActivityTabValue)}
        className="flex flex-col gap-4 flex-1 min-h-0"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TabsList>
              <TabsTrigger value="packages">Packages</TabsTrigger>
              <TabsTrigger value="repositories">Repositories</TabsTrigger>
            </TabsList>
            <WhatIsTrackedDialog tab={activeTab} />
          </div>
          <Select
            value={timeRange}
            onValueChange={value => setTimeRange(value as TimeRangeValue)}
          >
            <SelectTrigger className="w-[160px]" aria-label="Time range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="packages" className="flex flex-col min-h-0">
          {scopeSelectable && organizationId && (
            <div className="mb-3 space-y-3 empty:hidden">
              {/* `empty:hidden`: with no groups the selector draws nothing, and
              the page must look exactly as it did before (KIR-4701). */}
              <PolicyScopeSelector
                organizationId={organizationId}
                selectedGroupId={securityGroupId}
                onScopeChange={scope.setScope}
                variant="inventory"
                actions={
                  <GroupScopeResetButton
                    organizationId={organizationId}
                    groupId={securityGroupId}
                    groupName={scopeGroupName}
                    canEdit={canEditPackageLists}
                    target={packagesGroupScopeReset}
                  />
                }
              />
              {scope.requestedScopeUnavailable && (
                <InventoryScopeUnavailableNotice
                  onSwitchToOrganization={() => scope.setScope(null)}
                />
              )}
            </div>
          )}
          {/* Until a deep-linked `?scope=` resolves the group is unknown, and
              an edit would land on the organization: hold the table. A
              resolved group also waits for the package-lists check, which
              gates it: mounting first would show org rows, then reset the
              page to 1. */}
          {scope.isResolving ||
          (packageListsLoading && scope.groupId !== null) ? (
            <div data-testid="packages-scope-loading" className="px-4 py-2">
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <DataTable<AggregatedPackage>
              id={AGENT_COMMAND_TABLE_IDS.packages}
              mode="server"
              className="flex flex-col flex-1 min-h-0"
              containerClassName="flex-1"
              columns={packageColumns}
              initialState={initialState}
              // KIR-4443 (AC 10). A toolbar control rather than a column
              // filter: `listState` has no SQL column and no filter-options
              // endpoint behind it.
              toolbarEndSlot={
                showPackageLists ? (
                  <PackageListStateFilterControl
                    value={listState}
                    onChange={setListState}
                  />
                ) : undefined
              }
              onClearFilters={() => setListState('all')}
              pageResetKey={
                securityGroupId ? `${listState}|${securityGroupId}` : listState
              }
              search={{ placeholder: 'Search packages, sources, projects...' }}
              features={{ enableSorting: true, enableFiltering: true }}
              queryFn={packagesQueryFn}
              getQueryKeysFromParams={packagesQueryKeys}
              onRowClick={setDetailPackage}
              getRowClassName={getRowClassName}
              noDataLabel="No package installs recorded yet."
              errorBoundaryLabel="Something went wrong loading package activity"
            />
          )}
        </TabsContent>

        <TabsContent value="repositories" className="flex flex-col min-h-0">
          <DataTable<AggregatedRepository>
            id={AGENT_COMMAND_TABLE_IDS.repositories}
            mode="server"
            className="flex flex-col flex-1 min-h-0"
            containerClassName="flex-1"
            columns={gitColumns}
            initialState={initialState}
            search={{ placeholder: 'Search repositories, projects...' }}
            features={{ enableSorting: true, enableFiltering: true }}
            queryFn={reposQueryFn}
            getQueryKeysFromParams={reposQueryKeys}
            onRowClick={setDetailRepo}
            getRowClassName={getRowClassName}
            noDataLabel="No repository access recorded yet."
            errorBoundaryLabel="Something went wrong loading repository activity"
          />
        </TabsContent>
      </Tabs>

      {/* Aggregated Packages dialogs (KIR-3807 / KIR-3833). */}
      <AggregatedPackageDetailDialog
        organizationId={organizationId}
        pkg={detailPackage}
        onClose={() => setDetailPackage(null)}
        onShowUsers={setUsersPackage}
        onShowOccurrences={setOccurrencesPackage}
        onShowEndpoints={setEndpointsPackage}
      />
      <PackageOccurrencesDialog
        organizationId={organizationId}
        pkg={occurrencesPackage}
        onClose={() => setOccurrencesPackage(null)}
      />
      <PackageUsersDialog
        pkg={usersPackage}
        onClose={() => setUsersPackage(null)}
      />
      <PackageEndpointsDialog
        pkg={endpointsPackage}
        onClose={() => setEndpointsPackage(null)}
      />
      {/* Aggregated Repositories dialogs (KIR-3807 / KIR-3833). */}
      <AggregatedRepositoryDetailDialog
        organizationId={organizationId}
        repo={detailRepo}
        onClose={() => setDetailRepo(null)}
        onShowUsers={setUsersRepo}
        onShowOccurrences={setOccurrencesRepo}
        onShowEndpoints={setEndpointsRepo}
      />
      <RepositoryOccurrencesDialog
        organizationId={organizationId}
        repo={occurrencesRepo}
        onClose={() => setOccurrencesRepo(null)}
      />
      <RepositoryUsersDialog
        repo={usersRepo}
        onClose={() => setUsersRepo(null)}
      />
      <RepositoryEndpointsDialog
        repo={endpointsRepo}
        onClose={() => setEndpointsRepo(null)}
      />
    </div>
  );
}

AgentsActivityTab.displayName = 'AgentsActivityTab';
